import React, { useCallback } from 'react';
import { TextV1 } from '@withjoy/joykit';
import { DialogV1, useOverlayUnstable, ExtendedOverlayableProps } from '@withjoy/joykit';
import { EventPickerProps, useEventPickerController, UseEventPickerControllerArgs } from './EventPicker.controller';
import { EventPicker } from './EventPicker';
import { StyledDialog } from './EventPicker.styles';
import { EventMembership } from '@shared/components/AuthProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

const OVERLAY_KEY = 'event-picker';

const EventPickerDialogContent: React.FC<EventPickerProps & ExtendedOverlayableProps> = props => {
  const { overlayKey, hide, handleOnEventClick } = props;

  const onClose = useCallback(() => {
    hide(overlayKey);
  }, [hide, overlayKey]);

  const onEventClickAndHide = useCallback<typeof handleOnEventClick>(
    (...args) => {
      handleOnEventClick(...args);
      onClose();
    },
    [handleOnEventClick, onClose],
  );

  return (
    <StyledDialog
      overlayKey={overlayKey}
      canCloseOnOutsideClick={true}
      canCloseOnEscapeKey={true}
    >
      <DialogV1.Header
        title={
          <TextV1
            textAlign="left"
            lineHeight="32px"
            letterSpacing="-0.24px"
            fontWeight={600}
            fontStyle="normal"
            fontSize="24px"
            color={'black'}
            component="h1"
            marginY={6}
            marginX={7}
          >
            Select an Event
          </TextV1>
        }
        onClose={onClose}
      ></DialogV1.Header>
      <DialogV1.Body>
        <EventPicker
          {...props}
          handleOnEventClick={onEventClickAndHide}
        />
      </DialogV1.Body>
    </StyledDialog>
  );
};

export type UseEventPickerDialogArgs = {
  onOpen?: (memberships: EventMembership[]) => void;
} & UseEventPickerControllerArgs &
  Pick<EventPickerProps, 'currentEventId' | 'onCreateNewEvent'>;

export const useEventPickerDialog = ({ onEventClick, onOpen, membershipStatus, currentEventId, onCreateNewEvent }: UseEventPickerDialogArgs) => {
  const { handleOnEventClick, eventMemberships } = useEventPickerController({
    onEventClick,
    membershipStatus,
  });
  const [showEventPickerDialog, hideEventPickerDialog] = useOverlayUnstable<EventPickerProps>(
    {
      overlayKey: OVERLAY_KEY,
      render: props => <EventPickerDialogContent {...props} />,
      props: {
        handleOnEventClick,
        eventMemberships,
        currentEventId,
        onCreateNewEvent,
      },
    },
    {
      dependencies: [currentEventId, eventMemberships, handleOnEventClick, onCreateNewEvent],
    },
  );

  const wrappedShowEventPicker = useEventCallback(() => {
    onOpen?.(eventMemberships);
    showEventPickerDialog();
  });

  return [wrappedShowEventPicker, hideEventPickerDialog];
};
